import React from "react";
import {
  Calendar, Cake, Heart, Users, RefreshCw, ShieldCheck,
  AlertTriangle, Sparkles, MessageSquare, Filter, Database,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, FeatureCallout, ChecklistCard, FlowDiagram,
  SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogClinicallyAwarePatientGroups = () => (
  <RichBlogLayout
    category="Patient Communication"
    eyebrow="Marketing"
    title="Marketing Lists That Update Themselves: Clinically Aware Patient Groups in Asha"
    subtitle="Five auto-maintained groups, refreshed nightly from live chart data — with a built-in suppression layer that respects what just happened in the practice."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      The patient list you exported last Friday is already wrong. Two patients
      had a billing dispute over the weekend. Three more booked. One opted out
      of texts. By the time you load the spreadsheet on Monday morning, you're
      marketing to a practice that no longer exists.
    </Lead>

    <Prose>
      <p>
        Almost every dental marketing tool starts with the same ritual. Pull a
        report. Export a CSV. Filter the spreadsheet. Re-segment in the
        marketing platform. Send the campaign. Discover, three days later, that
        a patient on the "we miss you" list actually came in for a crown last
        week — and is now a little annoyed.
      </p>
      <p>
        The problem isn't the email. It's that the list was a snapshot, and the
        practice is a stream. Every appointment, every payment, every opt-out,
        every clinical note changes who the right people to talk to actually
        are. A list that doesn't move with the chart is a list that drifts out
        of context the moment it's built.
      </p>
      <p>
        Asha treats patient groups as live queries against the chart, not as
        spreadsheets. Five clinically aware groups, refreshed every night from
        the source of truth — with a suppression layer that quietly removes
        anyone who shouldn't be hearing from you today.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 412, label: "Patients sorted automatically" },
        { value: 5, label: "Clinically aware groups" },
        { value: 0, label: "Spreadsheets to maintain" },
        { value: 1, suffix: "x/night", label: "Refresh from live chart data" },
      ]}
    />

    <Prose>
      <h2>The five groups, and why they exist</h2>
      <p>
        Asha doesn't ship hundreds of generic segments. It ships five groups
        that map to the moments in dentistry where outreach actually matters —
        and lets the chart, not a marketer, decide who belongs in each one.
      </p>
    </Prose>

    <ParallelGrid
      title="The five clinically aware groups"
      columns={3}
      items={[
        { title: "New visit nurture", subtitle: "Recently booked first visit", icon: Calendar, status: "active" },
        { title: "Birthday outreach", subtitle: "Personal, time-windowed", icon: Cake, status: "active" },
        { title: "Post-visit follow-ups", subtitle: "After appointment completion", icon: Heart, status: "active" },
        { title: "New patient onboarding", subtitle: "Structured early engagement", icon: Users, status: "active" },
        { title: "Patient reactivation", subtitle: "Lapsed from regular care", icon: RefreshCw, status: "active" },
        { title: "Updated nightly", subtitle: "From the live chart, not a CSV", icon: Database, status: "done" },
      ]}
    />

    <Prose>
      <p>
        Each one is bound to a real event in the practice. New visit nurture
        starts the moment someone books their first appointment. Post-visit
        follow-ups appear when an appointment completes. Reactivation pulls
        patients who have fallen out of the recall cadence — not "all patients
        we haven't seen in a year," but the ones whose chart actually suggests
        they should be back.
      </p>
      <p>
        The point is not that these are the only segments worth running. The
        point is that none of them require anyone on your team to build, save,
        export, or rebuild a list. They are queries that maintain themselves.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Building lists by hand"
      afterTitle="Lists that read from the chart"
      beforeItems={[
        "Run multiple PMS reports and export them to CSV",
        "Filter, dedupe, and re-segment in a spreadsheet",
        "Upload to a marketing tool and recreate the segments",
        "Discover the data was already stale by the time it sent",
      ]}
      afterItems={[
        "Five groups that auto-populate from live chart events",
        "Refreshed nightly — no manual rebuild when the schedule changes",
        "A patient who books today is in the right group tomorrow",
        "A patient who just complained is silently removed from outreach",
      ]}
    />

    <Prose>
      <h2>The suppression layer is the quiet hero</h2>
      <p>
        It is easy to talk about who's in a marketing list. It is much harder —
        and far more important — to talk about who shouldn't be. A "we miss
        you" message landing on the phone of a patient who is actively
        disputing a $400 charge isn't just awkward. It's a trust event. The
        kind of small, careless message that quietly costs you a future
        booking.
      </p>
      <p>
        Asha applies the suppression layer automatically, before any group is
        sent. Three things have to be true for a patient to remain on a
        list.
      </p>
    </Prose>

    <ChecklistCard
      title="Suppression checks · run before every send"
      items={[
        { label: "Patient has not opted out of marketing or SMS", status: "pass" },
        { label: "No active financial dispute on the ledger", status: "pass" },
        { label: "No provider-level exclusion (clinical hold, sensitive case)", status: "pass" },
        { label: "No conflicting recent context (e.g. billing call within 48 hours)", status: "running" },
      ]}
    />

    <Prose>
      <p>
        These rules are not bolted on at the marketing platform. They live in
        the same place as the chart, the ledger, and the call logs — which
        means the suppression layer can see what the rest of the practice
        already knows. The patient who left a frustrated voicemail at 5:47 PM
        won't be on the birthday list at 8:00 AM. That's not a "feature." It's
        the bare minimum for marketing to feel like part of the practice
        instead of an outside agency.
      </p>
    </Prose>

    <PullQuote>
      A patient who just had a billing issue doesn't get a "we miss you"
      message the same day. That context is already built into the system.
    </PullQuote>

    <Prose>
      <h2>How a group goes from chart event to outbound message</h2>
      <p>
        It helps to walk a single patient through it. Maria came in for a
        crown seat on Tuesday. Tuesday night, the system rebuilds her group
        membership.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Chart event lands",
          body:
            "An appointment completes, a payment posts, a patient is added — the chart is the source of truth, and every group reads from it.",
        },
        {
          title: "Nightly refresh recalculates membership",
          body:
            "Every group rebuilds against current state. No exports, no spreadsheets, no manual rebuilds when something changes.",
        },
        {
          title: "Suppression layer runs across every list",
          body:
            "Opt-outs, financial disputes, and provider exclusions are subtracted before any audience is finalized.",
        },
        {
          title: "Outreach is drafted in tone-matched language",
          body:
            "The message references the right context — a recent visit, an upcoming birthday, a long absence — without being generic.",
        },
        {
          title: "Send happens with full chart provenance",
          body:
            "Every outbound message is linked back to the patient's chart, so the team can see exactly what was sent and why.",
        },
      ]}
    />

    <Prose>
      <p>
        Notice what is missing from that flow. There is no "marketing
        coordinator picks the segment," no "front desk pulls the report," no
        "let's double-check the opt-outs." The team's involvement is reduced to
        approving a tone, a template, or a campaign — not chasing a list.
      </p>

      <h2>What changes when context becomes the default</h2>
      <p>
        Once the chart is doing the segmenting, you stop seeing the small
        category of marketing mistakes that quietly damage relationships.
        The patient who got a recall reminder the day after their cleaning.
        The patient whose insurance was finally accepted, but who got a
        "switch insurance" email anyway. The patient who passed away and
        whose family received a "happy birthday" text six months later.
      </p>
      <p>
        These are not theoretical edge cases. They happen in every practice
        that runs marketing off of yesterday's spreadsheet. And they are
        exactly what a clinically aware system removes — not because someone
        added a rule, but because the system already knows.
      </p>
    </Prose>

    <FlowDiagram
      title="What happens behind every outbound campaign"
      nodes={[
        { label: "Live chart data", sublabel: "Source of truth", icon: Database },
        { label: "Group rebuild", sublabel: "Nightly refresh", icon: RefreshCw },
        { label: "Suppression filter", sublabel: "Opt-outs · disputes · exclusions", icon: Filter },
        { label: "Tone-matched send", sublabel: "Logged on the chart", icon: MessageSquare },
      ]}
    />

    <Prose>
      <h2>What's left for the office manager to do</h2>
      <p>
        For most practices, marketing is owned by whoever has time on a given
        Tuesday — usually the office manager, sometimes a part-time
        coordinator. They don't need a more sophisticated marketing tool;
        they need to stop building lists. With Asha, the lists are already
        built, already current, and already filtered for context.
      </p>
      <p>
        That frees the team to do the part of marketing that is genuinely
        human: choosing what to say to whom, deciding when to push a holiday
        campaign, reviewing the tone of a reactivation message before it goes
        out. The list management, the cleanup, the suppression — all of that
        is taken off the desk.
      </p>
    </Prose>

    <FeatureCallout
      stat="0 spreadsheets"
      label="From manual rebuilds to live chart-driven groups"
      description="Five clinically aware groups, refreshed nightly, with a suppression layer that respects what just happened in the practice."
      icon={Sparkles}
    />

    <Prose>
      <p>
        It is rare for marketing software to feel like part of clinical
        operations. It usually feels adjacent — a separate tool, a separate
        workflow, a separate list. Asha collapses that distance by making the
        chart itself the segmentation engine. The rest is just sending the
        right message to the right patient at a moment that makes sense.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Marketing in a dental practice fails most often not because the
        message is wrong, but because the list is wrong. Asha removes the
        list as something humans have to maintain — and removes the awkward
        outbound moments that come from talking to patients without
        knowing what just happened to them.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogClinicallyAwarePatientGroups;
